import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Shield, Mail, RefreshCw, ArrowRight } from 'lucide-react';
import LogoWithText from '../components/LogoWithText';
import authUtils from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function OTPVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [email, setEmail] = useState(location.state?.email || '');
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Check if we have a token, if not redirect to login
    const token = authUtils.getToken();
    console.log('[OTPVerify] Token present:', !!token);
    
    if (!token && !email) {
      console.log('[OTPVerify] No token and no email, redirecting to login');
      navigate('/login');
      return;
    }

    // If we have email from state, use it
    if (location.state?.email) {
      setEmail(location.state.email);
    } else if (token) {
      // Try to get email from /me endpoint
      checkAuthStatus();
    }
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      console.log('[OTPVerify] Auth status:', response.data);
      
      if (response.data.twofa_verified) {
        navigate('/dashboard');
      } else {
        setEmail(response.data.email);
      }
    } catch (error) {
      console.error('[OTPVerify] Auth check failed:', error);
      // Don't redirect immediately - might just be 2FA not verified yet
      if (!email) {
        navigate('/login');
      }
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const pastedValue = value.slice(0, 6).split('');
      const newOtp = [...otp];
      pastedValue.forEach((char, i) => {
        if (index + i < 6 && /^\d$/.test(char)) {
          newOtp[index + i] = char;
        }
      });
      setOtp(newOtp);
      const nextIndex = Math.min(index + pastedValue.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length !== 6) {
      toast.error('Please enter all 6 digits');
      return;
    }

    setIsVerifying(true);
    try {
      console.log('[OTPVerify] Verifying OTP...');
      const response = await axios.post(`${API}/auth/verify-otp`, { otp_code: code });

      console.log('[OTPVerify] Verification response:', {
        verified: response.data.verified,
        hasToken: !!response.data.session_token
      });

      // Store the verified session token
      if (response.data.session_token) {
        authUtils.setToken(response.data.session_token);
      }

      toast.success('Verification successful!');
      navigate('/dashboard');
    } catch (error) {
      console.error('[OTPVerify] Verification error:', error);
      toast.error(error.response?.data?.detail || 'Invalid OTP code');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;

    setIsResending(true);
    try {
      await axios.post(`${API}/auth/resend-otp`, {});
      toast.success('New OTP code sent to your email');
      setCountdown(60);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setIsResending(false);
    }
  };

  // Auto-submit when all digits entered
  useEffect(() => {
    if (otp.every(digit => digit !== '')) {
      handleVerify();
    }
  }, [otp]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                <Shield className="w-8 h-8 text-indigo-700" />
              </div>
            </div>
            <h1 className="text-2xl font-heading font-bold text-slate-900 mb-2">
              Two-Factor Authentication
            </h1>
            <p className="text-slate-600 text-sm">
              Enter the 6-digit code sent to
            </p>
            <p className="text-indigo-700 font-medium flex items-center justify-center space-x-2 mt-1">
              <Mail className="w-4 h-4" />
              <span>{email}</span>
            </p>
          </div>

          <div className="flex justify-center space-x-3 mb-8">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                disabled={isVerifying}
                data-testid={`otp-input-${index}`}
              />
            ))}
          </div>

          <button
            onClick={handleVerify}
            disabled={isVerifying || otp.some(d => !d)}
            className="w-full py-3 bg-indigo-700 text-white font-semibold rounded-xl hover:bg-indigo-800 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="verify-otp-button"
          >
            {isVerifying ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <span>Verify & Continue</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500 mb-2">
              Didn't receive the code?
            </p>
            <button
              onClick={handleResend}
              disabled={isResending || countdown > 0}
              className="text-indigo-700 hover:text-indigo-800 font-medium text-sm flex items-center justify-center space-x-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="resend-otp-button"
            >
              <RefreshCw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
              <span>
                {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
              </span>
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-100">
            <p className="text-xs text-slate-400 text-center">
              For security, the code expires in 10 minutes.
              <br />
              Check your spam folder if you don&apos;t see the email.
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Protected by Two-Factor Authentication
        </p>
      </motion.div>
    </div>
  );
}

export default OTPVerifyPage;
